import { create } from "zustand";
import type { DeckSpec, DeckCritique, ArtDirection } from "../lib/claude";
import type { DeckImages, DeckAspect } from "../lib/pptxBuilder";

export type JobStatus = "idle" | "generating" | "critiquing" | "refining" | "done" | "error";

export interface DeckJob {
  taskId: string;
  status: JobStatus;
  log: string[];
  deck: DeckSpec | null;
  images: DeckImages;
  artDirection: ArtDirection | null;
  critique: DeckCritique | null;
  aspect: DeckAspect;
  agentId: string;
  startedAt: number;
  updatedAt: number;
}

interface State {
  jobs: Record<string, DeckJob>;            // taskId → job
  getOrInit: (taskId: string, defaults: Partial<DeckJob>) => DeckJob;
  update: (taskId: string, patch: Partial<DeckJob>) => void;
  appendLog: (taskId: string, line: string) => void;
  setImages: (taskId: string, images: DeckImages) => void;
  patchImage: (taskId: string, idx: number, dataUrl: string | null) => void;
  remove: (taskId: string) => void;
  /** existe job ativo pra esta task? */
  isActive: (taskId: string) => boolean;
}

const blank = (taskId: string, defaults: Partial<DeckJob> = {}): DeckJob => ({
  taskId,
  status: "idle",
  log: [],
  deck: null,
  images: {},
  artDirection: null,
  critique: null,
  aspect: "4:5",
  agentId: "",
  startedAt: Date.now(),
  updatedAt: Date.now(),
  ...defaults
});

export const useDeckJobs = create<State>((set, get) => ({
  jobs: {},
  getOrInit: (taskId, defaults) => {
    const existing = get().jobs[taskId];
    if (existing) return existing;
    const j = blank(taskId, defaults);
    set(s => ({ jobs: { ...s.jobs, [taskId]: j } }));
    return j;
  },
  update: (taskId, patch) => set(s => {
    const cur = s.jobs[taskId] ?? blank(taskId);
    return { jobs: { ...s.jobs, [taskId]: { ...cur, ...patch, updatedAt: Date.now() } } };
  }),
  appendLog: (taskId, line) => set(s => {
    const cur = s.jobs[taskId] ?? blank(taskId);
    return { jobs: { ...s.jobs, [taskId]: { ...cur, log: [...cur.log, line], updatedAt: Date.now() } } };
  }),
  setImages: (taskId, images) => set(s => {
    const cur = s.jobs[taskId] ?? blank(taskId);
    return { jobs: { ...s.jobs, [taskId]: { ...cur, images, updatedAt: Date.now() } } };
  }),
  patchImage: (taskId, idx, dataUrl) => set(s => {
    const cur = s.jobs[taskId] ?? blank(taskId);
    const next = { ...cur.images };
    if (dataUrl === null) delete next[idx];
    else next[idx] = dataUrl;
    return { jobs: { ...s.jobs, [taskId]: { ...cur, images: next, updatedAt: Date.now() } } };
  }),
  remove: (taskId) => set(s => {
    const next = { ...s.jobs }; delete next[taskId];
    return { jobs: next };
  }),
  isActive: (taskId) => {
    const j = get().jobs[taskId];
    return !!j && (j.status === "generating" || j.status === "critiquing" || j.status === "refining");
  }
}));
